import { useMemo } from 'react';
import { Panel, PanelHeader, PanelInset, PanelTitle, TableTd, TableTr } from '@customerio/pluma-components/react';
import { useActivityLogEnvironment } from '@/services/db/activity-log/queries';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { activityLogTypeLabel } from '@/pages/workspaces/activity-logs/activity-log-types';
import { NameCell, TypeCell } from '@/pages/workspaces/activity-logs/components/cells';
import { TimestampCell } from '@/pages/workspaces/activity-logs/components/columns';
import { ActivityLogTable, useObjectActivityLogs, type ActivityLogTableColumn } from './activity-log-table';
import { useObjectDetail } from './context';

const RECENT_ACTIVITY_LIMIT = 5;

const COLUMNS: readonly ActivityLogTableColumn[] = [
  { label: 'Activity type' },
  { label: 'Activity name' },
  { label: 'Timestamp', textAlign: 'right' },
];

export function ObjectRecentActivity() {
  const { record, workspaceId } = useObjectDetail();
  const taxonomy = useProductTaxonomy();
  const { identifiers } = useActivityLogEnvironment(String(workspaceId));
  const { query, rows } = useObjectActivityLogs(workspaceId, record.identifiers.cio_object_id, {
    limit: RECENT_ACTIVITY_LIMIT,
  });

  const typeTerms = useMemo(
    () => ({
      campaignSingular: taxonomy.term('campaigns.titleSingular'),
      personSingular: taxonomy.term('people.titleSingular'),
      personPlural: taxonomy.term('people.titlePlural'),
    }),
    [taxonomy],
  );

  return (
    <Panel>
      <PanelHeader pb="0" aria-labelledby="recent-activity-header">
        <PanelTitle id="recent-activity-header" size="md">
          Recent Activity
        </PanelTitle>
      </PanelHeader>
      <PanelInset pb="0">
        <ActivityLogTable
          caption="Recent Activity Table"
          columns={COLUMNS}
          query={query}
          errorMessage="Could not load recent attribute changes."
          rows={rows}
          renderRow={(row) => (
            <TableTr key={row.log.id}>
              <TableTd>
                <TypeCell
                  row={row}
                  label={activityLogTypeLabel(row.log, typeTerms)}
                  workspaceId={String(workspaceId)}
                  personSingular={typeTerms.personSingular}
                />
              </TableTd>
              <TableTd>
                <NameCell row={row} workspaceId={String(workspaceId)} identifiers={identifiers} />
              </TableTd>
              <TableTd textAlign="right">
                <TimestampCell value={row.log.timestamp} />
              </TableTd>
            </TableTr>
          )}
        />
      </PanelInset>
    </Panel>
  );
}
